// ReportImage — real render of a schema-v2 `image` report block
// (return-knowledge-images-across-surfaces, PR 4 Task 5, contract fixup).
//
// Renders 1-3 server-resolved `ReportImageRelation`s per block, positionally
// correlated with the block's `ImageAssetRef`s (see
// `groupReportVisualRelationsByBlock` in `@/lib/reports` for why: the shared
// backend `SafeVisualRelationProjection` DTO has no `handle` field, so the
// caller — `ReportBlock.tsx`/`ExecutiveReport.tsx` — passes each block a
// `relations` array already sliced to that exact block, in asset-ref order).
// Presented in the block's bounded presentation enum
// (`inline | wide | gallery`) — the renderer owns the actual CSS, never the
// model. Every relation slot is one of three rendered states:
//   - "available": real preview via a same-origin `blob:` object URL
//     (`useAuthedBlobUrls` + the injected `fetchImageBlob`), trusted
//     alt/caption, and a keyboard-accessible lightbox (the shared
//     `ImageLightbox`).
//   - "redacted": the audience's projection policy withheld this relation.
//     Discloses nothing beyond "visual evidence was not shared" — no title,
//     id, or metadata, per spec.
//   - "unavailable": no relation occupies this position at all (a stale/
//     mismatched projection), or the byte fetch itself failed — same
//     neutral tile as "redacted", never a broken `<img>` pointing outside.
//
// The lightbox is the app's one shared `ImageLightbox`
// (`unify-image-lightbox`), the same component chat uses; the tile/grid markup
// is NOT reused as-is: report images have their own presentation enum, their
// own redacted state (chat response assets have no such thing), and render
// inside the report's deterministic visual grammar (`--report-*` CSS
// variables) rather than the chat surface's `border-border`/`bg-muted`
// tokens.
//
// One lightbox per BLOCK, over the block's "available" relations in position
// order (`unify-image-lightbox`, spec `reports`): previous/next,
// `ArrowLeft`/`ArrowRight` and an `n de N` indicator come from the shared
// component, and a block with a single available image gets none of them. To
// give the tiles and the lightbox ONE source of bytes, the rendition fetching
// lives at the block (`useAuthedBlobUrls`) and the tiles are presentational:
// opening a thumbnail never fetches again, and walking the set never fetches
// at all. Redacted relations and positions with no relation stay inert tiles
// OUTSIDE the set; an available relation whose rendition failed keeps its
// neutral "No disponible" tile but stays IN the set, where its slide shows
// the same unavailable state in place (no details, no broken `<img>`).
//
// Locator description and "download original" (return-knowledge-images-
// across-surfaces PR 4 gap closure #2): the shared `SafeVisualRelationProjection`
// backend DTO now carries a trusted `locator` (page/block/timestamp only —
// `describeAssetLocator`, the exact same wording `ResponseAssetGrid.tsx`
// uses for chat) and an `original_available` UI signal, populated only on
// the "available" branch. `original_available` is always `false` for a
// public relation (enforced backend-side,
// `project_public_visual_relation`), so this affordance never renders for
// an anonymous viewer even though the code path is shared.

import {
  ImageLightbox,
  type LightboxImage,
  type LightboxSlide,
} from "@/components/ui/image-lightbox";
import { describeAssetLocator } from "@/lib/asset-locator-labels";
import type {
  ImageBlock,
  ReportImageBlobFetcher,
  ReportImageRelation,
  ReportImageRelationAvailable,
} from "@/lib/reports";
import { triggerBlobDownload } from "@/lib/reports";
import { lightboxImageOf, useAuthedBlobUrls } from "@/lib/use-authed-blob-urls";
import { Download, EyeOff, ImageOff, Loader } from "lucide-react";
import { type JSX, useState } from "react";

type Presentation = ImageBlock["presentation"];

const CONTAINER_CLASS: Record<Presentation, string> = {
  inline: "flex flex-wrap gap-3",
  wide: "flex flex-col gap-4",
  gallery: "grid grid-cols-2 gap-3 sm:grid-cols-3",
};

const TILE_SIZE_CLASS: Record<Presentation, string> = {
  inline: "h-32 w-32 shrink-0",
  wide: "aspect-video w-full",
  gallery: "aspect-square w-full",
};

function NeutralTile({
  presentation,
  icon,
  label,
  testId,
}: {
  presentation: Presentation;
  icon: JSX.Element;
  label: string;
  testId: string;
}): JSX.Element {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border border-[var(--report-border)] bg-[var(--report-muted)] p-3 text-center text-xs text-[var(--report-muted-fg)] ${TILE_SIZE_CLASS[presentation]}`}
      data-testid={testId}
    >
      {icon}
      <span>{label}</span>
    </div>
  );
}

function isAvailable(
  relation: ReportImageRelation | undefined,
): relation is ReportImageRelationAvailable {
  return relation?.state === "available";
}

interface ReportImageTileProps {
  relation: ReportImageRelation | undefined;
  slotKey: string;
  presentation: Presentation;
  /** Where the block's rendition fetch stands; `null` for a relation that is not available. */
  image: LightboxImage | null;
  onOpen: () => void;
}

function ReportImageTile({
  relation,
  slotKey,
  presentation,
  image,
  onOpen,
}: ReportImageTileProps): JSX.Element {
  if (relation?.state === "redacted") {
    return (
      <NeutralTile
        presentation={presentation}
        icon={<EyeOff size={18} aria-hidden />}
        label="Evidencia visual no compartida"
        testId={`report-image-redacted-${relation.relation_id}`}
      />
    );
  }

  // Missing relation (no entry at this slot in the grouped projection) or a
  // rendition fetch failure both degrade to the same neutral tile — never a
  // broken `<img>` pointing outside.
  if (relation === undefined || image?.status === "unavailable") {
    return (
      <NeutralTile
        presentation={presentation}
        icon={<ImageOff size={18} aria-hidden />}
        label="No disponible"
        testId={`report-image-unavailable-${slotKey}`}
      />
    );
  }

  if (image === null || image.status === "loading") {
    return (
      <div
        className={`flex animate-pulse items-center justify-center rounded-lg border border-[var(--report-border)] bg-[var(--report-muted)] ${TILE_SIZE_CLASS[presentation]}`}
        data-testid={`report-image-loading-${relation.relation_id}`}
      >
        <Loader size={16} className="animate-spin text-[var(--report-muted-fg)]" aria-hidden />
      </div>
    );
  }

  return (
    <div
      className={`overflow-hidden rounded-lg ${TILE_SIZE_CLASS[presentation]}`}
      data-testid={`report-image-available-${relation.relation_id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="h-full w-full overflow-hidden rounded-lg border border-[var(--report-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--report-accent)]"
        aria-label={`Ver imagen: ${relation.alt_text}`}
        title={relation.alt_text}
      >
        <img
          src={image.url}
          alt={relation.alt_text}
          loading="lazy"
          className="h-full w-full object-cover transition-opacity hover:opacity-90"
        />
      </button>
    </div>
  );
}

// What sits under the image in the lightbox: trusted caption, locator and — only
// when the projection offers it — the original download. The slide keys this
// component by relation id, so the in-flight `downloading` state belongs to one
// image and never bleeds onto the next slide.
function ReportImageDetails({
  relation,
  fetchImageBlob,
}: {
  relation: ReportImageRelationAvailable;
  fetchImageBlob: ReportImageBlobFetcher;
}): JSX.Element {
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadOriginal(): Promise<void> {
    if (!relation.original_available || downloading) return;
    setDownloading(true);
    try {
      const blob = await fetchImageBlob(relation.relation_id, "original");
      const safeName = (relation.citation_label ?? "imagen").replace(/[^a-zA-Z0-9-]/g, "");
      triggerBlobDownload(blob, `${safeName || "imagen"}-${relation.relation_id}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      {relation.caption ? <p className="text-sm leading-6">{relation.caption}</p> : null}
      {relation.locator ? (
        <p className="text-label-2 text-[var(--report-muted-fg)]">
          {describeAssetLocator(relation.locator)}
        </p>
      ) : null}
      {relation.original_available ? (
        <button
          type="button"
          onClick={() => void handleDownloadOriginal()}
          disabled={downloading}
          aria-label="Descargar original"
          className="inline-flex w-fit items-center gap-1 rounded-md border border-[var(--report-border)] px-2 py-1 text-body-3 text-[var(--report-fg)] hover:bg-[var(--report-muted)] disabled:opacity-50"
        >
          {downloading ? (
            <Loader size={14} className="animate-spin" aria-hidden />
          ) : (
            <Download size={14} aria-hidden />
          )}
          Descargar original
        </button>
      ) : null}
    </>
  );
}

interface ReportImageBlockViewProps {
  block: ImageBlock;
  relations: ReportImageRelation[];
  fetchImageBlob: ReportImageBlobFetcher;
}

export function ReportImageBlockView({
  block,
  relations,
  fetchImageBlob,
}: ReportImageBlockViewProps): JSX.Element {
  // `relation_id` of the open image (`null` = closed).
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // The lightbox set: this block's available relations, in position order.
  const slotRelations = block.assets.map((_, position) => relations[position]);
  const available = slotRelations.filter(isAvailable);
  const blobs = useAuthedBlobUrls(
    available.map((relation) => relation.relation_id),
    (key) => fetchImageBlob(key, "rendition"),
  );

  const slides: LightboxSlide[] = available.map((relation) => {
    const image = lightboxImageOf(blobs.get(relation.relation_id));
    return {
      key: relation.relation_id,
      alt: relation.alt_text,
      image,
      details:
        image.status === "unavailable" ? undefined : (
          <ReportImageDetails
            key={relation.relation_id}
            relation={relation}
            fetchImageBlob={fetchImageBlob}
          />
        ),
    };
  });

  return (
    <div className={CONTAINER_CLASS[block.presentation]}>
      {block.assets.map((assetRef, position) => {
        const relation = slotRelations[position];
        const availableRelation = isAvailable(relation) ? relation : null;
        return (
          <ReportImageTile
            key={assetRef.handle}
            relation={relation}
            slotKey={`${block.block_id}-${position}`}
            presentation={block.presentation}
            image={
              availableRelation === null
                ? null
                : lightboxImageOf(blobs.get(availableRelation.relation_id))
            }
            onOpen={() => {
              if (availableRelation !== null) setActiveKey(availableRelation.relation_id);
            }}
          />
        );
      })}
      <ImageLightbox
        slides={slides}
        activeKey={activeKey}
        onActiveKeyChange={setActiveKey}
        onClose={() => setActiveKey(null)}
      />
    </div>
  );
}
